/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { groupBy } from 'lodash'
import { defineMessages } from 'react-intl'

import type { BasicFailedReplyElement } from '@modules/cloud-api/v1/types'
import type { SliderInstanceType } from '@modules/ui-types/sliders'

const messages = defineMessages({
  foundInTiers: {
    id: 'stack-deployment-update-warnings.warning-found-in-tiers',
    defaultMessage: 'found in {tierCount} tiers',
  },
})

/**
 * Aggregates warnings with same message but different topology tiers. Since we allow for per-tier
 * configuration, we need to aggregate warnings that are repeated in different tiers. User settings
 * are generally meant for all tiers so it's quite common to have the same warning repeated in
 * different tiers.
 * @param warnings Array of warning objects
 * @returns Array of aggregated warnings with count property
 */
export function aggregateWarningsByMessage(
  warnings: Array<BasicFailedReplyElement & { sliderInstanceType?: SliderInstanceType }>,
): Array<BasicFailedReplyElement & { sliderInstanceType?: SliderInstanceType }> {
  // Simply group warnings by their exact message text
  const groupedWarnings = groupBy(warnings, (warning) => warning.message)

  // this above gives us an object with message as key and array of warnings as value
  // we just need to return a new object that has a single value from the array and appended
  // to it, we have the count of the number of times it has shown up

  // Convert grouped warnings to array with count information
  return Object.entries(groupedWarnings).map(([_key, items]) => {
    const [firstItem] = items
    // For empty groups (which shouldn't happen with groupBy), return minimal object

    if (firstItem === undefined) {
      return {
        code: '',
        message: '',
      }
    }

    // Create base warning object from the first item with required fields
    const baseWarning: BasicFailedReplyElement & { sliderInstanceType?: SliderInstanceType } = {
      ...firstItem,
    }

    // If there are multiple items with the same message, append the count
    if (items.length > 1) {
      baseWarning.message = `${baseWarning.message} (${messages.foundInTiers.defaultMessage.replace(
        '{tierCount}',
        String(items.length),
      )})`
    }

    return baseWarning
  })
}
